// ─────────────────────────────────────────────────────────────────
// Control Systems — Steady-State Error
// ─────────────────────────────────────────────────────────────────

const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// Shared arrow marker helper
// ─────────────────────────────────────────────────────────────────
function Arrowhead({ id, color }) {
  return (
    <marker id={id} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
      <polygon points="0 0,7 3,0 6" fill={color}/>
    </marker>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 1 — What Is Steady-State Error? (visual intuition)
// ─────────────────────────────────────────────────────────────────
function DiagramWhatIsSSE() {
  const W = 900, H = 380;

  // Three panels: step, ramp, parabola inputs with system responses
  // System for step: Type 0, Kp=2  → ess = 1/(1+2) = 0.333
  // System for ramp: Type 1, Kv=2  → ess = 1/2 = 0.5 (lag)
  // System for parabola: Type 2, Ka=2 → ess = 1/2 (constant error)

  const N = 150, tMax = 6;

  // Panel 1: Step input, Type 0 response (approaches 0.667 not 1.0)
  const stepInput = [];
  const stepResp  = [];
  for (let i = 0; i <= N; i++) {
    const t = i * tMax / N;
    stepInput.push({ t, y: t >= 0.5 ? 1.0 : 0 });
    stepResp.push({ t, y: t >= 0.5 ? (1 - 1/3) * (1 - Math.exp(-(t-0.5)*2.5)) + 0 : 0 });
  }
  // Final value ≈ 0.667
  const stepFinal = (1 - 1/3);

  // Panel 2: Ramp input, Type 1 response (parallel ramp with offset)
  const rampInput = [];
  const rampResp  = [];
  for (let i = 0; i <= N; i++) {
    const t = i * tMax / N;
    rampInput.push({ t, y: t });
    // Type 1: response tracks ramp with fixed lag e_ss = 1/Kv = 0.5
    const ess = 0.5;
    const transient = t < 0.01 ? 0 : ess * (1 - Math.exp(-t * 3));
    rampResp.push({ t, y: Math.max(0, t - ess + ess * Math.exp(-t * 3)) });
  }

  // Panel 3: Parabola input, Type 2 response
  const parabInput = [];
  const parabResp  = [];
  for (let i = 0; i <= N; i++) {
    const t = i * tMax / N;
    parabInput.push({ t, y: 0.5 * t * t });
    // Type 2: Constant acceleration error = 1/Ka
    const ess = 0.5;
    parabResp.push({ t, y: Math.max(0, 0.5 * t * t - ess * (1 - Math.exp(-t * 2))) });
  }

  const panels = [
    {
      ox: 15, title: 'Step Input  r(t)=u(t)',
      subtitle: 'Type 0 system',
      inputPts: stepInput, inputColor: '#2E75B6', inputLabel: 'r(t)=1',
      respPts:  stepResp,  respColor:  '#c0392b', respLabel: 'y(t)',
      essLabel: 'e_ss=0.33', essY: stepFinal, essRef: 1.0,
      note: 'Output never reaches 1 — finite position error',
      yMax: 1.4,
    },
    {
      ox: 315, title: 'Ramp Input  r(t)=t',
      subtitle: 'Type 1 system',
      inputPts: rampInput, inputColor: '#2E75B6', inputLabel: 'r(t)=t',
      respPts:  rampResp,  respColor:  '#c0392b', respLabel: 'y(t)',
      essLabel: 'e_ss=0.5', essY: null, essRef: null,
      note: 'Constant lag — parallel ramps, fixed offset',
      yMax: 6.5,
    },
    {
      ox: 615, title: 'Parabola  r(t)=t²/2',
      subtitle: 'Type 2 system',
      inputPts: parabInput, inputColor: '#2E75B6', inputLabel: 'r(t)=t²/2',
      respPts:  parabResp,  respColor:  '#c0392b', respLabel: 'y(t)',
      essLabel: 'e_ss=0.5', essY: null, essRef: null,
      note: 'Constant accel. error — grows with curvature',
      yMax: 18.5,
    },
  ];

  const PW = 272, PH = 210;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Steady-State Error — Visual Definition for Three Standard Input Types</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arrowhead id="sse-blk" color="#aaa"/>
          <Arrowhead id="sse-red" color="#c0392b"/>
          <Arrowhead id="sse-blu" color="#2E75B6"/>
          <clipPath id="sse-c0"><rect x={16}  y={40} width={270} height={212}/></clipPath>
          <clipPath id="sse-c1"><rect x={316} y={40} width={270} height={212}/></clipPath>
          <clipPath id="sse-c2"><rect x={616} y={40} width={270} height={212}/></clipPath>
        </defs>

        {panels.map((p, pi) => {
          const clips = ['url(#sse-c0)','url(#sse-c1)','url(#sse-c2)'];
          const clip  = clips[pi];
          const sx = t  => p.ox + 32 + (t / tMax) * (PW - 44);
          const sy = yv => p.ox === 15 ? (40 + PH - 18 - (yv / p.yMax) * (PH - 28))
                                       : (40 + PH - 18 - (yv / p.yMax) * (PH - 28));

          const inpD = p.inputPts.map(({ t, y }, i) =>
            `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');
          const resD = p.respPts.map(({ t, y }, i) =>
            `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');

          return (
            <g key={pi}>
              <rect x={p.ox} y={30} width={PW} height={PH+8} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
              <text x={p.ox+PW/2} y={22} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">{p.title}</text>

              {/* Axes */}
              <line x1={p.ox+30} y1={40+PH-16} x2={p.ox+PW-8} y2={40+PH-16} stroke="#bbb" strokeWidth="1.3" markerEnd="url(#sse-blk)"/>
              <line x1={p.ox+30} y1={40+PH-16} x2={p.ox+30}   y2={42}        stroke="#bbb" strokeWidth="1.3" markerEnd="url(#sse-blk)"/>
              <text x={p.ox+PW-4} y={40+PH-12} fontSize={8} fill="#aaa">t</text>
              <text x={p.ox+34}   y={44}         fontSize={8} fill="#aaa">y</text>

              {/* Input */}
              <path d={inpD} fill="none" stroke={p.inputColor} strokeWidth="2" strokeDasharray="6,3" clipPath={clip}/>

              {/* Response */}
              <path d={resD} fill="none" stroke={p.respColor} strokeWidth="2.5" clipPath={clip}/>

              {/* Error bracket for step */}
              {pi === 0 && (
                <g>
                  <line x1={sx(5.6)} y1={sy(stepFinal)} x2={sx(5.6)} y2={sy(1.0)}
                    stroke="#e07b00" strokeWidth="2" markerStart="url(#sse-red)" markerEnd="url(#sse-red)"/>
                  <text x={sx(5.6)+6} y={(sy(stepFinal)+sy(1.0))/2+4} fontSize={9} fill="#e07b00" fontWeight="700">e_ss</text>
                  <line x1={p.ox+30} y1={sy(1.0)} x2={p.ox+PW-10} y2={sy(1.0)}
                    stroke="#2E75B6" strokeWidth="1" strokeDasharray="4,3" opacity="0.6"/>
                  <text x={p.ox+34} y={sy(1.0)-4} fontSize={8} fill="#2E75B6">r=1</text>
                  <line x1={p.ox+30} y1={sy(stepFinal)} x2={p.ox+PW-10} y2={sy(stepFinal)}
                    stroke="#c0392b" strokeWidth="1" strokeDasharray="4,3" opacity="0.6"/>
                  <text x={p.ox+34} y={sy(stepFinal)-4} fontSize={8} fill="#c0392b">y(∞)=0.67</text>
                </g>
              )}

              {/* Ramp lag bracket */}
              {pi === 1 && (
                <g>
                  {/* vertical error at t=5 */}
                  <line x1={sx(5)} y1={sy(rampResp[Math.round(5/tMax*N)]?.y ?? 4.5)}
                        x2={sx(5)} y2={sy(5)}
                    stroke="#e07b00" strokeWidth="2" markerStart="url(#sse-red)" markerEnd="url(#sse-red)"/>
                  <text x={sx(5)+5} y={sy(4.75)+4} fontSize={9} fill="#e07b00" fontWeight="700">e_ss=0.5</text>
                </g>
              )}

              {/* Parabola error bracket */}
              {pi === 2 && (
                <g>
                  <line x1={sx(4.5)} y1={sy(parabResp[Math.round(4.5/tMax*N)]?.y ?? 9.5)}
                        x2={sx(4.5)} y2={sy(0.5*4.5*4.5)}
                    stroke="#e07b00" strokeWidth="2" markerStart="url(#sse-red)" markerEnd="url(#sse-red)"/>
                  <text x={sx(4.5)+5} y={sy(9.8)+4} fontSize={9} fill="#e07b00" fontWeight="700">e_ss</text>
                </g>
              )}

              {/* Labels */}
              <text x={p.ox+36} y={50} fontSize={9} fill={p.inputColor}>{p.inputLabel}</text>
              <text x={p.ox+36} y={62} fontSize={9} fill={p.respColor}>{p.respLabel}</text>

              {/* Bottom note */}
              <rect x={p.ox+2} y={40+PH-2} width={PW-4} height={34} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="0.8"/>
              <text x={p.ox+PW/2} y={40+PH+12} textAnchor="middle" fontSize={8.5} fill="#7B0D0D" fontWeight="700">{p.subtitle}</text>
              <text x={p.ox+PW/2} y={40+PH+25} textAnchor="middle" fontSize={8}   fill="#555">{p.note}</text>
            </g>
          );
        })}

        {/* Bottom definition box */}
        <rect x={10} y={272} width={880} height={100} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={290} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Definition of Steady-State Error</text>
        <text x={450} y={308} textAnchor="middle" fontSize={11} fill="#333" fontFamily="'Courier New',monospace">e_ss = lim  e(t)  =  lim  [r(t) − y(t)]     (if the limit exists)</text>
        <text x={450} y={322} textAnchor="middle" fontSize={9}  fill="#aaa">t→∞                   t→∞</text>
        <text x={450} y={338} textAnchor="middle" fontSize={11} fill="#333" fontFamily="'Courier New',monospace">E(s) = R(s) − Y(s) = R(s) · [1 − T(s)]  =  R(s) / [1 + G(s)H(s)]</text>
        <text x={450} y={356} textAnchor="middle" fontSize={10} fill="#555">Final Value Theorem:   e_ss = lim s·E(s)  —  valid only if sE(s) has all poles in open LHP</text>
        <text x={450} y={370} textAnchor="middle" fontSize={9}  fill="#aaa">(limit as s→0)</text>
      </svg>
      <div style={S.dCaption}>Steady-state error is the difference between the desired reference r(t) and the actual output y(t) as time approaches infinity. For a step input the error appears as a constant gap; for a ramp it appears as a constant lag offset; for a parabola as a growing offset. The type of the open-loop transfer function determines which inputs can be tracked with zero error.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 2 — Final Value Theorem & Error Signal Block Diagram
// ─────────────────────────────────────────────────────────────────
function DiagramFVT() {
  const W = 900, H = 370;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Final Value Theorem &amp; Closed-Loop Error Transfer Function</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arrowhead id="fvt-blk" color="#555"/>
          <Arrowhead id="fvt-red" color="#c0392b"/>
          <Arrowhead id="fvt-blu" color="#2E75B6"/>
          <Arrowhead id="fvt-grn" color="#27ae60"/>
        </defs>

        {/* ── LEFT: Block diagram deriving E(s) ── */}
        <rect x={10} y={10} width={430} height={220} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={225} y={28} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Closed-Loop Error Derivation</text>

        {/* R(s) input */}
        <text x={18} y={105} fontSize={11} fill="#c0392b" fontWeight="700">R(s)</text>
        <line x1={42} y1={101} x2={76} y2={101} stroke="#c0392b" strokeWidth="2" markerEnd="url(#fvt-red)"/>

        {/* Summing junction */}
        <circle cx={94} cy={101} r={18} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={94} y={106} textAnchor="middle" fontSize={16} fill="#c0392b" fontWeight="700">Σ</text>
        <text x={77}  y={97}  fontSize={11} fill="#27ae60" fontWeight="700">+</text>
        <text x={90}  y={119} fontSize={11} fill="#c0392b" fontWeight="700">−</text>

        {/* E(s) */}
        <line x1={112} y1={101} x2={156} y2={101} stroke="#555" strokeWidth="2" markerEnd="url(#fvt-blk)"/>
        <text x={118} y={94} fontSize={10} fill="#555">E(s)</text>

        {/* G(s) block */}
        <rect x={156} y={81} width={90} height={40} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={201} y={99} textAnchor="middle" fontSize={12} fill="#2E75B6" fontWeight="700">G(s)</text>
        <text x={201} y={113} textAnchor="middle" fontSize={9}  fill="#555">forward path</text>
        <line x1={246} y1={101} x2={310} y2={101} stroke="#555" strokeWidth="2" markerEnd="url(#fvt-blk)"/>

        {/* Y(s) */}
        <circle cx={310} cy={101} r={4} fill="#555"/>
        <line x1={314} y1={101} x2={420} y2={101} stroke="#27ae60" strokeWidth="2" markerEnd="url(#fvt-grn)"/>
        <text x={340} y={94} fontSize={11} fill="#27ae60" fontWeight="700">Y(s)</text>

        {/* H(s) feedback */}
        <line x1={310} y1={105} x2={310} y2={168} stroke="#555" strokeWidth="1.5"/>
        <rect x={238} y={152} width={80} height={32} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1.5"/>
        <text x={278} y={171} textAnchor="middle" fontSize={11} fill="#9b59b6" fontWeight="700">H(s)</text>
        <line x1={238} y1={168} x2={94} y2={168} stroke="#555" strokeWidth="1.5"/>
        <line x1={94} y1={168} x2={94} y2={119} stroke="#555" strokeWidth="1.5" markerEnd="url(#fvt-blk)"/>

        {/* Derivation steps */}
        <rect x={18} y={195} width={414} height={28} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={225} y={207} textAnchor="middle" fontSize={10} fill="#333" fontFamily="'Courier New',monospace">Y(s) = G(s)·E(s)   |   E(s) = R(s) − H(s)·Y(s)   →   E(s) = R(s)·[1/(1+G(s)H(s))]</text>
        <text x={225} y={219} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontFamily="'Courier New',monospace">Error TF:  E(s)/R(s) = 1/(1+G(s)H(s))   ←   the key formula</text>

        {/* ── RIGHT: FVT derivation & conditions ── */}
        <rect x={452} y={10} width={438} height={220} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={671} y={28} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Final Value Theorem (FVT)</text>

        {[
          { y:46,  fill:'#FEF0F0', bc:'#c0392b',
            lines:['FVT statement:','lim f(t) = lim s·F(s)    (as t→∞ and s→0)',
                   'Requires: all poles of s·F(s) in open LHP'] },
          { y:102, fill:'#eaf3ff', bc:'#2E75B6',
            lines:['Apply to E(s):','e_ss = lim s·E(s) = lim s·R(s)/(1+G(s)H(s))',
                   '             s→0               s→0'] },
          { y:152, fill:'#f0fff4', bc:'#27ae60',
            lines:['Validity check:','s·E(s) must have ALL poles with Re < 0',
                   'If closed loop is unstable: FVT gives wrong answer!',
                   'Always verify stability BEFORE applying FVT'] },
        ].map(({ y, fill, bc, lines }) => (
          <g key={y}>
            <rect x={460} y={y} width={422} height={lines.length*18+10} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            {lines.map((l, j) => (
              <text key={j} x={468} y={y+15+j*18} fontSize={j===0?11:10} fill={j===0?'#7B0D0D':'#333'}
                fontWeight={j===0?'700':'400'} fontFamily={j>0?"'Courier New',monospace":"'Georgia',serif"}>{l}</text>
            ))}
          </g>
        ))}

        {/* ── BOTTOM: Three input types worked ── */}
        <rect x={10} y={240} width={880} height={120} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={258} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Applying FVT to Standard Inputs — Unity Feedback (H=1)</text>

        {[
          { label:'Step  R(s)=1/s',    formula:'e_ss = lim s·(1/s)/(1+G(s)) = 1/(1+Kp)',       Kdef:'Kp = lim G(s) = G(0)',       color:'#c0392b', x:18 },
          { label:'Ramp  R(s)=1/s²',   formula:'e_ss = lim s·(1/s²)/(1+G(s)) = 1/Kv',          Kdef:'Kv = lim s·G(s)',            color:'#2E75B6', x:308 },
          { label:'Parabola  R(s)=1/s³',formula:'e_ss = lim s·(1/s³)/(1+G(s)) = 1/Ka',         Kdef:'Ka = lim s²·G(s)',           color:'#27ae60', x:598 },
        ].map(({ label, formula, Kdef, color, x }) => (
          <g key={x}>
            <rect x={x+2} y={266} width={282} height={88} rx={3} fill={color+'10'} stroke={color} strokeWidth="1"/>
            <text x={x+10} y={281} fontSize={10}  fill={color} fontWeight="700">{label}</text>
            <text x={x+10} y={296} fontSize={9.5} fill="#333" fontFamily="'Courier New',monospace">{formula}</text>
            <line x1={x+2} y1={302} x2={x+282} y2={302} stroke={color} strokeWidth="0.5" opacity="0.5"/>
            <text x={x+10} y={315} fontSize={9}   fill="#555">(s→0 for all terms)</text>
            <text x={x+10} y={330} fontSize={9.5} fill={color} fontFamily="'Courier New',monospace">{Kdef}</text>
            <text x={x+10} y={348} fontSize={8.5} fill="#555">(s→0)</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The error transfer function E(s)/R(s) = 1/(1+G(s)H(s)) is derived directly from the closed-loop block diagram. Applying the Final Value Theorem (lim as s→0) converts this to a steady-state number. The three error constants — position Kp, velocity Kv, and acceleration Ka — are evaluated by taking limits of G(s), s·G(s), and s²·G(s) as s→0. The FVT is only valid when the closed-loop system is stable.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 3 — System Type: Pole Locations & Classification
// ─────────────────────────────────────────────────────────────────
function DiagramSystemType() {
  const W = 900, H = 370;

  // For each type show: open-loop pole-zero map + Bode low-freq slope
  const types = [
    {
      label: 'Type 0', color: '#c0392b', bg: '#FEF0F0',
      example: 'G(s) = K / (s+1)(s+3)',
      poles: [{ r:-1, i:0 }, { r:-3, i:0 }], zeros: [],
      integrators: 0,
      step_ess:   '1/(1+Kp)',
      ramp_ess:   '∞',
      parab_ess:  '∞',
      bodeLowSlope: '0 dB/dec',
    },
    {
      label: 'Type 1', color: '#2E75B6', bg: '#eaf3ff',
      example: 'G(s) = K / s(s+2)',
      poles: [{ r:0, i:0 }, { r:-2, i:0 }], zeros: [],
      integrators: 1,
      step_ess:   '0',
      ramp_ess:   '1/Kv',
      parab_ess:  '∞',
      bodeLowSlope: '−20 dB/dec',
    },
    {
      label: 'Type 2', color: '#27ae60', bg: '#f0fff4',
      example: 'G(s) = K / s²(s+4)',
      poles: [{ r:0, i:0 }, { r:0, i:0.15 }, { r:-4, i:0 }], zeros: [],
      integrators: 2,
      step_ess:   '0',
      ramp_ess:   '0',
      parab_ess:  '1/Ka',
      bodeLowSlope: '−40 dB/dec',
    },
  ];

  const PW = 270, PH = 140;
  const cx = 100, cy = 70, sc = 45;
  const px = (r, ox) => ox + cx + r * sc;
  const py = (im)    => cy - im * sc;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>System Type — Number of Open-Loop Integrators &amp; Effect on Steady-State Error</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arrowhead id="st-blk" color="#aaa"/>
        </defs>

        {/* Three type panels */}
        {types.map((tp, ti) => {
          const ox = 10 + ti * 293;
          return (
            <g key={ti}>
              <rect x={ox} y={10} width={PW} height={PH+18} rx={5} fill={tp.bg} stroke={tp.color} strokeWidth="2"/>
              <rect x={ox} y={10} width={PW} height={24} rx={5} fill={tp.color}/>
              <text x={ox+PW/2} y={26} textAnchor="middle" fontSize={12} fill="#fff" fontWeight="700">{tp.label} — {tp.integrators} Integrator{tp.integrators!==1?'s':''}</text>

              {/* Example */}
              <text x={ox+8} y={47} fontSize={9.5} fill={tp.color} fontWeight="700">Example:</text>
              <text x={ox+70} y={47} fontSize={9.5} fill="#333" fontFamily="'Courier New',monospace">{tp.example}</text>

              {/* s-plane mini */}
              <rect x={ox+8} y={52} width={PW-16} height={PH-18} rx={3} fill="#fafafa" stroke="#ddd" strokeWidth="1"/>

              {/* Axes */}
              <line x1={ox+18} y1={52+cy} x2={ox+PW-10} y2={52+cy} stroke="#ccc" strokeWidth="1" markerEnd="url(#st-blk)"/>
              <line x1={ox+18+cx} y1={54} x2={ox+18+cx} y2={52+PH-20} stroke="#ccc" strokeWidth="1" markerEnd="url(#st-blk)"/>
              <text x={ox+PW-6}  y={52+cy+4} fontSize={7} fill="#bbb">σ</text>
              <text x={ox+18+cx+3} y={58}   fontSize={7} fill="#bbb">jω</text>

              {/* Stability regions */}
              <rect x={ox+18} y={54} width={cx} height={PH-22} fill="#27ae60" opacity="0.04"/>
              <rect x={ox+18+cx} y={54} width={PW-26-cx} height={PH-22} fill="#c0392b" opacity="0.04"/>

              {/* Poles */}
              {tp.poles.map((p, pi) => (
                <g key={pi}>
                  <text x={ox+18+px(p.r, 0)} y={52+py(p.i)+4}
                    textAnchor="middle" fontSize={16} fill={tp.color} fontWeight="700">×</text>
                </g>
              ))}

              {/* Integrator annotation */}
              {tp.integrators > 0 && (
                <text x={ox+18+cx+4} y={52+cy-6} fontSize={8} fill={tp.color} fontWeight="700">
                  {tp.integrators === 1 ? '1 pole at s=0' : '2 poles at s=0'}
                </text>
              )}

              {/* Bode slope annotation */}
              <text x={ox+PW-8} y={52+PH-22} textAnchor="end" fontSize={8.5} fill={tp.color} fontWeight="700">Low-ω: {tp.bodeLowSlope}</text>
            </g>
          );
        })}

        {/* Error table */}
        <rect x={10} y={178} width={880} height={182} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={196} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Steady-State Error by System Type (Unity Feedback, H(s)=1)</text>

        {/* Header row */}
        <rect x={10} y={202} width={880} height={26} fill="#c0392b" rx={2}/>
        {['Input Signal','R(s)','Type 0','Type 1','Type 2'].map((h, i) => (
          <text key={i} x={[30,170,340,510,680][i]} y={219} fontSize={11} fill="#fff" fontWeight="700">{h}</text>
        ))}

        {[
          { input:'Step (position)',    Rs:'1/s',  t0:'1/(1+Kp)',  t1:'0',         t2:'0',     bg:'#fff5f5' },
          { input:'Ramp (velocity)',    Rs:'1/s²', t0:'∞',        t1:'1/Kv',      t2:'0',     bg:'#fff' },
          { input:'Parabola (accel.)',  Rs:'1/s³', t0:'∞',        t1:'∞',         t2:'1/Ka',  bg:'#fff5f5' },
        ].map(({ input, Rs, t0, t1, t2, bg }, i) => {
          const rowY = 228 + i * 42;
          return (
            <g key={i}>
              <rect x={10} y={rowY} width={880} height={42} fill={bg} stroke="#f0cece" strokeWidth="0.8"/>
              <text x={30}  y={rowY+16} fontSize={10} fill="#7B0D0D" fontWeight="700">{input}</text>
              <text x={30}  y={rowY+30} fontSize={9}  fill="#555" fontFamily="'Courier New',monospace">R(s) = {Rs}</text>
              <text x={170} y={rowY+23} fontSize={11} fill="#555"  fontFamily="'Courier New',monospace">{Rs}</text>

              {/* Type 0 */}
              <rect x={310} y={rowY+4} width={155} height={34} rx={2}
                fill={t0==='0'?'#f0fff4':t0==='∞'?'#FEF0F0':'#fff5e6'}
                stroke={t0==='0'?'#27ae60':t0==='∞'?'#c0392b':'#e07b00'} strokeWidth="1"/>
              <text x={387} y={rowY+26} textAnchor="middle" fontSize={11} fill={t0==='0'?'#27ae60':t0==='∞'?'#c0392b':'#e07b00'}
                fontFamily="'Courier New',monospace" fontWeight="700">{t0}</text>

              {/* Type 1 */}
              <rect x={478} y={rowY+4} width={155} height={34} rx={2}
                fill={t1==='0'?'#f0fff4':t1==='∞'?'#FEF0F0':'#fff5e6'}
                stroke={t1==='0'?'#27ae60':t1==='∞'?'#c0392b':'#e07b00'} strokeWidth="1"/>
              <text x={555} y={rowY+26} textAnchor="middle" fontSize={11} fill={t1==='0'?'#27ae60':t1==='∞'?'#c0392b':'#e07b00'}
                fontFamily="'Courier New',monospace" fontWeight="700">{t1}</text>

              {/* Type 2 */}
              <rect x={646} y={rowY+4} width={155} height={34} rx={2}
                fill={t2==='0'?'#f0fff4':t2==='∞'?'#FEF0F0':'#fff5e6'}
                stroke={t2==='0'?'#27ae60':t2==='∞'?'#c0392b':'#e07b00'} strokeWidth="1"/>
              <text x={723} y={rowY+26} textAnchor="middle" fontSize={11} fill={t2==='0'?'#27ae60':t2==='∞'?'#c0392b':'#e07b00'}
                fontFamily="'Courier New',monospace" fontWeight="700">{t2}</text>
            </g>
          );
        })}

        {/* Legend row */}
        <rect x={10} y={354} width={880} height={18} rx={2} fill="#f5f5f5"/>
        <text x={20}  y={366} fontSize={9} fill="#27ae60" fontWeight="700">0 = Zero steady-state error</text>
        <text x={220} y={366} fontSize={9} fill="#e07b00" fontWeight="700">1/(1+Kp) etc. = Finite error (depends on gain)</text>
        <text x={580} y={366} fontSize={9} fill="#c0392b" fontWeight="700">∞ = Infinite error (system cannot track this input)</text>
      </svg>
      <div style={S.dCaption}>System type is the number of free integrators (poles at s=0) in the open-loop transfer function G(s)H(s). Each integrator provides one order of free integration: Type 0 has finite position error; Type 1 eliminates position error but has finite velocity error; Type 2 eliminates both position and velocity error but has finite acceleration error. Higher type = better tracking, but harder to stabilize.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 4 — Error Constants Kp, Kv, Ka: Computed Curves
// ─────────────────────────────────────────────────────────────────
function DiagramErrorConstants() {
  const W = 900, H = 370;
  const N = 250, tMax = 8;

  // ── Simulate three Type 0 systems with different Kp under step input ──
  // G(s) = K/(s+1)  →  Kp = K  →  e_ss = 1/(1+K)
  // Closed-loop step: y(t) = (K/(K+1))(1 - exp(-(K+1)t))
  const kpVals   = [0.5, 2, 10];
  const kpColors = ['#c0392b','#e07b00','#27ae60'];
  const kpESS    = kpVals.map(k => 1/(1+k));

  const kpPaths = kpVals.map(k => {
    const final = k / (k + 1);
    let d = '';
    for (let i = 0; i <= N; i++) {
      const t = i * tMax / N;
      const y = final * (1 - Math.exp(-(k+1)*t));
      const x = (30 + 32 + (t/tMax)*240).toFixed(1);
      const yp = (30 + 230 - 20 - y * 200).toFixed(1);
      d += `${i===0?'M':'L'}${x},${yp} `;
    }
    return d.trim();
  });

  // ── Type 1 ramp responses with different Kv ──
  // G(s) = Kv/s  →  closed-loop: y(t) = t - (1/Kv)(1-exp(-Kv·t))
  const kvVals   = [0.5, 2, 5];
  const kvColors = ['#c0392b','#2E75B6','#9b59b6'];

  const kvPaths = kvVals.map(kv => {
    let d = '';
    for (let i = 0; i <= N; i++) {
      const t = i * tMax / N;
      const y = Math.max(0, t - (1/kv)*(1 - Math.exp(-kv*t)));
      const x = (330 + 32 + (t/tMax)*240).toFixed(1);
      const yp = (30 + 230 - 20 - y * 28).toFixed(1);
      d += `${i===0?'M':'L'}${x},${yp} `;
    }
    return d.trim();
  });
  const kvRampPath = (() => {
    let d = '';
    for (let i = 0; i <= N; i++) {
      const t = i * tMax / N;
      const x = (330 + 32 + (t/tMax)*240).toFixed(1);
      const yp = (30 + 230 - 20 - t * 28).toFixed(1);
      d += `${i===0?'M':'L'}${x},${yp} `;
    }
    return d.trim();
  })();

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Error Constants Kp, Kv, Ka — Effect of Gain on Steady-State Error</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arrowhead id="ec-blk" color="#aaa"/>
          <clipPath id="ec-c0"><rect x={60}  y={30} width={250} height={235}/></clipPath>
          <clipPath id="ec-c1"><rect x={360} y={30} width={250} height={235}/></clipPath>
        </defs>

        {/* ── LEFT: Kp effect on step error ── */}
        <rect x={28} y={10} width={274} height={280} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={165} y={26} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Kp Effect — Step Input, Type 0</text>

        {/* Steady-state line */}
        <line x1={62} y1={30+210-200} x2={278} y2={30+210-200} stroke="#aaa" strokeWidth="1" strokeDasharray="5,4"/>
        <text x={281} y={30+210-196} fontSize={8} fill="#aaa">r=1</text>

        {/* Axes */}
        <line x1={62} y1={262} x2={282} y2={262} stroke="#aaa" strokeWidth="1.3" markerEnd="url(#ec-blk)"/>
        <line x1={62} y1={262} x2={62}  y2={28}  stroke="#aaa" strokeWidth="1.3" markerEnd="url(#ec-blk)"/>
        <text x={286} y={266} fontSize={8} fill="#aaa">t</text>
        <text x={65}  y={25}  fontSize={8} fill="#aaa">y(t)</text>

        {kpPaths.map((d, i) => (
          <path key={i} d={d} fill="none" stroke={kpColors[i]} strokeWidth="2.2" clipPath="url(#ec-c0)"/>
        ))}

        {/* Final value lines & error labels */}
        {kpVals.map((k, i) => {
          const final = k/(k+1);
          const yp = 30 + 230 - 20 - final*200;
          return (
            <g key={i}>
              <line x1={62} y1={yp} x2={278} y2={yp} stroke={kpColors[i]} strokeWidth="1" strokeDasharray="4,3" opacity="0.5"/>
              <text x={282} y={yp+3} fontSize={8} fill={kpColors[i]} fontWeight="700">{final.toFixed(2)}</text>
              <line x1={250+i*10} y1={yp} x2={250+i*10} y2={30+210-200}
                stroke={kpColors[i]} strokeWidth="1.5" markerStart="url(#ec-blk)" markerEnd="url(#ec-blk)"/>
            </g>
          );
        })}

        {/* Legend */}
        {kpVals.map((k, i) => (
          <g key={i}>
            <line x1={36} y1={274+i*12} x2={54} y2={274+i*12} stroke={kpColors[i]} strokeWidth="2"/>
            <text x={58} y={278+i*12} fontSize={8.5} fill={kpColors[i]}>Kp={k}, e_ss={kpESS[i].toFixed(3)}</text>
          </g>
        ))}

        {/* ── MIDDLE: Kv effect on ramp ── */}
        <rect x={328} y={10} width={274} height={280} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={465} y={26} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Kv Effect — Ramp Input, Type 1</text>

        <line x1={362} y1={262} x2={582} y2={262} stroke="#aaa" strokeWidth="1.3" markerEnd="url(#ec-blk)"/>
        <line x1={362} y1={262} x2={362} y2={28}  stroke="#aaa" strokeWidth="1.3" markerEnd="url(#ec-blk)"/>
        <text x={586} y={266} fontSize={8} fill="#aaa">t</text>

        {/* Ideal ramp */}
        <path d={kvRampPath} fill="none" stroke="#aaa" strokeWidth="1.5" strokeDasharray="6,4" clipPath="url(#ec-c1)"/>

        {kvPaths.map((d, i) => (
          <path key={i} d={d} fill="none" stroke={kvColors[i]} strokeWidth="2.2" clipPath="url(#ec-c1)"/>
        ))}

        {kvVals.map((kv, i) => (
          <g key={i}>
            <line x1={336} y1={275+i*12} x2={354} y2={275+i*12} stroke={kvColors[i]} strokeWidth="2"/>
            <text x={358} y={279+i*12} fontSize={8.5} fill={kvColors[i]}>Kv={kv}, e_ss={(1/kv).toFixed(3)}</text>
          </g>
        ))}

        {/* ── RIGHT: Error constants summary ── */}
        <rect x={614} y={10} width={278} height={360} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={753} y={28} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Error Constants — Definitions</text>

        {[
          {
            name:'Position Error Constant Kp', color:'#c0392b', bg:'#FEF0F0',
            formula:'Kp = lim G(s)H(s)',
            sublabel:'(s → 0)',
            used:'e_ss (step) = 1/(1+Kp)',
            type0:'Kp = G(0) (finite)',
            type1:'Kp = ∞  →  e_ss = 0',
            y: 38,
          },
          {
            name:'Velocity Error Constant Kv', color:'#2E75B6', bg:'#eaf3ff',
            formula:'Kv = lim s·G(s)H(s)',
            sublabel:'(s → 0)',
            used:'e_ss (ramp) = 1/Kv',
            type0:'Kv = 0  →  e_ss = ∞',
            type1:'Kv = K (finite)',
            y: 148,
          },
          {
            name:'Acceleration Error Const. Ka', color:'#27ae60', bg:'#f0fff4',
            formula:'Ka = lim s²·G(s)H(s)',
            sublabel:'(s → 0)',
            used:'e_ss (parabola) = 1/Ka',
            type0:'Ka = 0  →  e_ss = ∞',
            type1:'Ka = 0  →  e_ss = ∞',
            y: 258,
          },
        ].map(({ name, color, bg, formula, sublabel, used, type0, type1, y }) => (
          <g key={y}>
            <rect x={622} y={y} width={262} height={104} rx={3} fill={bg} stroke={color} strokeWidth="1.2"/>
            <text x={630} y={y+15} fontSize={10}  fill={color} fontWeight="700">{name}</text>
            <text x={630} y={y+32} fontSize={10}  fill="#333" fontFamily="'Courier New',monospace">{formula}</text>
            <text x={630} y={y+45} fontSize={8.5} fill="#aaa">{sublabel}</text>
            <line x1={622} y1={y+52} x2={884} y2={y+52} stroke={color} strokeWidth="0.6" opacity="0.4"/>
            <text x={630} y={y+65} fontSize={9.5} fill="#7B0D0D" fontWeight="700">{used}</text>
            <text x={630} y={y+80} fontSize={9}   fill="#555" fontFamily="'Courier New',monospace">{type0}</text>
            <text x={630} y={y+95} fontSize={9}   fill="#555" fontFamily="'Courier New',monospace">{type1}</text>
          </g>
        ))}

        <rect x={622} y={368} width={262} height={0} rx={2}/>
      </svg>
      <div style={S.dCaption}>Position constant Kp controls step-input steady-state error: larger Kp reduces error but for Type 0 it can never reach zero (left panel). Velocity constant Kv controls ramp-tracking error: larger Kv reduces the constant lag offset (middle panel). These two panels show why simply increasing gain helps but never fully solves the problem — only adding integrators (increasing system type) can drive certain error terms to exactly zero.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 5 — Non-Unity Feedback: H(s) ≠ 1
// ─────────────────────────────────────────────────────────────────
function DiagramNonUnity() {
  const W = 900, H = 360;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Non-Unity Feedback — Generalised Steady-State Error when H(s) ≠ 1</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arrowhead id="nu-blk" color="#555"/>
          <Arrowhead id="nu-red" color="#c0392b"/>
          <Arrowhead id="nu-grn" color="#27ae60"/>
          <Arrowhead id="nu-blu" color="#2E75B6"/>
          <Arrowhead id="nu-pur" color="#9b59b6"/>
        </defs>

        {/* ── LEFT: Non-unity block diagram ── */}
        <rect x={10} y={10} width={430} height={210} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={225} y={28} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Non-Unity Feedback Block Diagram</text>

        {/* R(s) */}
        <text x={18} y={104} fontSize={11} fill="#c0392b" fontWeight="700">R(s)</text>
        <line x1={42} y1={101} x2={78} y2={101} stroke="#c0392b" strokeWidth="2" markerEnd="url(#nu-red)"/>

        {/* Summing junction */}
        <circle cx={96} cy={101} r={18} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={96} y={106} textAnchor="middle" fontSize={16} fill="#c0392b" fontWeight="700">Σ</text>
        <text x={79}  y={97}  fontSize={11} fill="#27ae60" fontWeight="700">+</text>
        <text x={92}  y={119} fontSize={11} fill="#c0392b" fontWeight="700">−</text>

        {/* E(s) */}
        <line x1={114} y1={101} x2={152} y2={101} stroke="#555" strokeWidth="2" markerEnd="url(#nu-blk)"/>
        <text x={120} y={94} fontSize={10} fill="#555">E(s)</text>

        {/* G(s) */}
        <rect x={152} y={81} width={90} height={40} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={197} y={98} textAnchor="middle" fontSize={12} fill="#2E75B6" fontWeight="700">G(s)</text>
        <text x={197} y={113} textAnchor="middle" fontSize={9}  fill="#555">plant+controller</text>

        <line x1={242} y1={101} x2={310} y2={101} stroke="#555" strokeWidth="2" markerEnd="url(#nu-blk)"/>

        {/* Y(s) */}
        <circle cx={310} cy={101} r={4} fill="#555"/>
        <line x1={314} y1={101} x2={428} y2={101} stroke="#27ae60" strokeWidth="2" markerEnd="url(#nu-grn)"/>
        <text x={345} y={94} fontSize={11} fill="#27ae60" fontWeight="700">Y(s)</text>

        {/* H(s) block — non-unity */}
        <line x1={310} y1={105} x2={310} y2={165} stroke="#555" strokeWidth="1.5"/>
        <rect x={238} y={150} width={80} height={32} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="2"/>
        <text x={278} y={165} textAnchor="middle" fontSize={12} fill="#9b59b6" fontWeight="700">H(s)</text>
        <text x={278} y={178} textAnchor="middle" fontSize={8}  fill="#9b59b6">sensor/filter</text>
        <text x={320} y={145} fontSize={8.5} fill="#9b59b6" fontStyle="italic">H(s) ≠ 1  !</text>
        <line x1={238} y1={166} x2={96} y2={166} stroke="#555" strokeWidth="1.5"/>
        <line x1={96}  y1={166} x2={96} y2={119} stroke="#555" strokeWidth="1.5" markerEnd="url(#nu-blk)"/>

        {/* Derivation */}
        <rect x={18} y={192} width={414} height={20} rx={2} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={225} y={206} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontFamily="'Courier New',monospace">
          E(s) = R(s) · [1/(1+G(s)H(s))]     ←  error is at the summing junction, NOT the output error
        </text>

        {/* ── RIGHT: Reduction to unity feedback ── */}
        <rect x={452} y={10} width={438} height={210} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={671} y={28} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Converting to Equivalent Unity Feedback</text>

        {/* Equivalent form */}
        <text x={462} y={50} fontSize={10} fill="#7B0D0D" fontWeight="700">Step 1: Compute closed-loop TF</text>
        <text x={462} y={65} fontSize={10} fill="#333" fontFamily="'Courier New',monospace">T(s) = G(s)H(s) / [1+G(s)H(s)]  ×  1/H(s)</text>

        <text x={462} y={88} fontSize={10} fill="#7B0D0D" fontWeight="700">Step 2: Actual output error = r(t) − y(t)</text>
        <text x={462} y={103} fontSize={10} fill="#333" fontFamily="'Courier New',monospace">Y(s)/R(s) = G(s)/[1+G(s)H(s)]</text>
        <text x={462} y={118} fontSize={10} fill="#333" fontFamily="'Courier New',monospace">Output error TF = 1 − G(s)/[1+G(s)H(s)]</text>
        <text x={462} y={133} fontSize={10} fill="#333" fontFamily="'Courier New',monospace">             = [1+G(s)H(s)−G(s)] / [1+G(s)H(s)]</text>

        <text x={462} y={156} fontSize={10} fill="#7B0D0D" fontWeight="700">Step 3: Apply FVT to output error</text>
        <rect x={460} y={162} width={422} height={24} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={671} y={178} textAnchor="middle" fontSize={11} fill="#2E75B6" fontFamily="'Courier New',monospace">e_ss(output) = lim s·R(s)·[1+G(s)(H(s)−1)] / [1+G(s)H(s)]</text>
        <text x={671} y={200} textAnchor="middle" fontSize={9.5} fill="#555">s→0</text>

        {/* ── BOTTOM: Important distinctions ── */}
        <rect x={10} y={230} width={880} height={120} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={248} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Actuating Signal Error vs. Actual Output Error</text>

        {[
          {
            x: 18, color:'#c0392b', bg:'#FEF0F0',
            title:'Actuating Signal Error  E(s)',
            body:'E(s) = R(s) − B(s) = R(s) − H(s)Y(s)\ne_ss = lim s·R(s)/(1+G(s)H(s))\nThis is what the controller sees — NOT the true output error\nFor H(s)≠1: this differs from r(t)−y(t)!',
          },
          {
            x: 453, color:'#2E75B6', bg:'#eaf3ff',
            title:'True Output Error  ε(s) = R(s) − Y(s)',
            body:'ε_ss = lim s·R(s)·[1+G(s)H(s)−G(s)] / [1+G(s)H(s)]\nFor H(s)=1: reduces to standard e_ss = lim s·R(s)/(1+G)\nFor H(0)=1 (unity DC gain sensor): same as unity feedback\nFor H(0)≠1: sensor scale error appears in steady state',
          },
        ].map(({ x, color, bg, title, body }) => (
          <g key={x}>
            <rect x={x+2} y={256} width={430} height={88} rx={3} fill={bg} stroke={color} strokeWidth="1.2"/>
            <text x={x+10} y={271} fontSize={10} fill={color} fontWeight="700">{title}</text>
            {body.split('\n').map((l, j) => (
              <text key={j} x={x+10} y={285+j*15} fontSize={9} fill="#333"
                fontFamily="'Courier New',monospace">{l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>When the feedback sensor H(s)≠1, there are two different error signals: the actuating signal error E(s)=R(s)−H(s)Y(s) (what the controller uses) and the true output error ε(s)=R(s)−Y(s). These are equal only when H(s)=1. For a sensor with DC gain H(0)≠1 (e.g. a tachometer that reads 10× actual speed), there will be a permanent output error even if the controller drives its actuating error to zero.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 6 — SSE with Disturbances
// ─────────────────────────────────────────────────────────────────
function DiagramDisturbances() {
  const W = 900, H = 370;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Steady-State Error Due to Disturbances — Input &amp; Output Disturbances</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arrowhead id="dist-blk" color="#555"/>
          <Arrowhead id="dist-red" color="#c0392b"/>
          <Arrowhead id="dist-grn" color="#27ae60"/>
          <Arrowhead id="dist-pur" color="#9b59b6"/>
          <Arrowhead id="dist-orn" color="#e07b00"/>
        </defs>

        {/* ── TOP: Block diagram with two disturbance points ── */}
        <rect x={10} y={10} width={880} height={200} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={28} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Block Diagram with Input Disturbance D(s) and Output Disturbance N(s)</text>

        {/* R(s) */}
        <text x={18} y={117} fontSize={11} fill="#c0392b" fontWeight="700">R(s)</text>
        <line x1={42} y1={113} x2={80} y2={113} stroke="#c0392b" strokeWidth="2" markerEnd="url(#dist-red)"/>

        {/* Summing jxn 1 */}
        <circle cx={98} cy={113} r={17} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={98} y={118} textAnchor="middle" fontSize={15} fill="#c0392b" fontWeight="700">Σ</text>
        <text x={82}  y={109} fontSize={10} fill="#27ae60">+</text>
        <text x={94}  y={130} fontSize={10} fill="#c0392b">−</text>

        {/* C(s) */}
        <line x1={115} y1={113} x2={148} y2={113} stroke="#555" strokeWidth="2" markerEnd="url(#dist-blk)"/>
        <rect x={148} y={94} width={80} height={38} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={188} y={111} textAnchor="middle" fontSize={12} fill="#c0392b" fontWeight="700">C(s)</text>
        <text x={188} y={125} textAnchor="middle" fontSize={9}  fill="#555">controller</text>

        {/* Summing jxn 2 — Input disturbance D(s) */}
        <line x1={228} y1={113} x2={270} y2={113} stroke="#555" strokeWidth="2" markerEnd="url(#dist-blk)"/>
        <circle cx={288} cy={113} r={17} fill="#fff5e6" stroke="#e07b00" strokeWidth="2"/>
        <text x={288} y={118} textAnchor="middle" fontSize={15} fill="#e07b00" fontWeight="700">Σ</text>
        <text x={272} y={109} fontSize={10} fill="#27ae60">+</text>
        <text x={284} y={130} fontSize={10} fill="#e07b00">+</text>

        {/* D(s) input — from bottom */}
        <line x1={288} y1={60} x2={288} y2={96} stroke="#e07b00" strokeWidth="2" strokeDasharray="5,3" markerEnd="url(#dist-orn)"/>
        <text x={296} y={55} fontSize={10} fill="#e07b00" fontWeight="700">D(s)</text>
        <text x={296} y={67} fontSize={8.5} fill="#555">Input disturbance</text>
        <text x={296} y={79} fontSize={8.5} fill="#555">(actuator noise)</text>

        {/* G1(s) plant part 1 */}
        <line x1={305} y1={113} x2={338} y2={113} stroke="#555" strokeWidth="2" markerEnd="url(#dist-blk)"/>
        <rect x={338} y={94} width={80} height={38} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={378} y={111} textAnchor="middle" fontSize={11} fill="#2E75B6" fontWeight="700">G₁(s)</text>
        <text x={378} y={125} textAnchor="middle" fontSize={9}  fill="#555">plant part 1</text>

        {/* Summing jxn 3 — Output disturbance N(s) */}
        <line x1={418} y1={113} x2={460} y2={113} stroke="#555" strokeWidth="2" markerEnd="url(#dist-blk)"/>
        <circle cx={478} cy={113} r={17} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="2"/>
        <text x={478} y={118} textAnchor="middle" fontSize={15} fill="#9b59b6" fontWeight="700">Σ</text>
        <text x={462} y={109} fontSize={10} fill="#27ae60">+</text>
        <text x={474} y={130} fontSize={10} fill="#9b59b6">+</text>

        {/* N(s) — from top */}
        <line x1={478} y1={60} x2={478} y2={96} stroke="#9b59b6" strokeWidth="2" strokeDasharray="5,3" markerEnd="url(#dist-pur)"/>
        <text x={486} y={55} fontSize={10} fill="#9b59b6" fontWeight="700">N(s)</text>
        <text x={486} y={67} fontSize={8.5} fill="#555">Output disturbance</text>
        <text x={486} y={79} fontSize={8.5} fill="#555">(sensor noise, wind)</text>

        {/* G2(s) */}
        <line x1={495} y1={113} x2={528} y2={113} stroke="#555" strokeWidth="2" markerEnd="url(#dist-blk)"/>
        <rect x={528} y={94} width={80} height={38} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={568} y={111} textAnchor="middle" fontSize={11} fill="#2E75B6" fontWeight="700">G₂(s)</text>
        <text x={568} y={125} textAnchor="middle" fontSize={9}  fill="#555">plant part 2</text>

        {/* Y(s) */}
        <line x1={608} y1={113} x2={680} y2={113} stroke="#555" strokeWidth="2" markerEnd="url(#dist-blk)"/>
        <circle cx={660} cy={113} r={4} fill="#555"/>
        <line x1={664} y1={113} x2={880} y2={113} stroke="#27ae60" strokeWidth="2" markerEnd="url(#dist-grn)"/>
        <text x={700} y={106} fontSize={11} fill="#27ae60" fontWeight="700">Y(s)</text>

        {/* H(s) feedback */}
        <line x1={660} y1={117} x2={660} y2={178} stroke="#555" strokeWidth="1.5"/>
        <rect x={568} y={164} width={80} height={30} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={608} y={183} textAnchor="middle" fontSize={11} fill="#27ae60" fontWeight="700">H(s)</text>
        <line x1={568} y1={179} x2={98}  y2={179} stroke="#555" strokeWidth="1.5"/>
        <line x1={98}  y1={179} x2={98}  y2={130} stroke="#555" strokeWidth="1.5" markerEnd="url(#dist-blk)"/>

        {/* Superposition label */}
        <rect x={18} y={182} width={860} height={22} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={450} y={196} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontFamily="'Courier New',monospace">
          Y(s) = T_R(s)·R(s) + T_D(s)·D(s) + T_N(s)·N(s)   →   Superposition: total output = sum of responses to each input
        </text>

        {/* ── BOTTOM: Three disturbance TF derivations ── */}
        <rect x={10} y={220} width={880} height={140} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={238} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Disturbance Transfer Functions &amp; Steady-State Errors</text>

        {[
          {
            x:18, color:'#c0392b', bg:'#FEF0F0',
            title:'Reference R(s) → Output (set D=N=0)',
            tf:'T_R = C(s)G₁(s)G₂(s) / [1+C(s)G₁(s)G₂(s)H(s)]',
            ess:'e_ss(R) = standard formula (Type / Kp / Kv / Ka)',
          },
          {
            x:308, color:'#e07b00', bg:'#fff5e6',
            title:'Input Disturbance D(s) (set R=N=0)',
            tf:'Y_D/D = G₁(s)G₂(s) / [1+C(s)G₁(s)G₂(s)H(s)]',
            ess:'e_ss(D) = −lim s·D(s)·G₁G₂/[1+CG₁G₂H] (s→0)',
          },
          {
            x:598, color:'#9b59b6', bg:'#f5d0ff',
            title:'Output Disturbance N(s) (set R=D=0)',
            tf:'Y_N/N = G₂(s) / [1+C(s)G₁(s)G₂(s)H(s)]',
            ess:'e_ss(N) = −lim s·N(s)·G₂/[1+CG₁G₂H] (s→0)',
          },
        ].map(({ x, color, bg, title, tf, ess }) => (
          <g key={x}>
            <rect x={x+2} y={246} width={282} height={106} rx={3} fill={bg} stroke={color} strokeWidth="1.2"/>
            <text x={x+10} y={262} fontSize={9.5} fill={color} fontWeight="700">{title}</text>
            <text x={x+10} y={278} fontSize={8.5} fill="#333" fontFamily="'Courier New',monospace">{tf}</text>
            <line x1={x+2} y1={285} x2={x+282} y2={285} stroke={color} strokeWidth="0.5" opacity="0.4"/>
            <text x={x+10} y={298} fontSize={9.5} fill="#7B0D0D" fontWeight="700">SSE contribution:</text>
            <text x={x+10} y={312} fontSize={8.5} fill="#555" fontFamily="'Courier New',monospace">{ess}</text>
            <rect x={x+6} y={318} width={270} height={28} rx={2} fill="#fff" stroke={color} strokeWidth="0.8"/>
            <text x={x+141} y={328} textAnchor="middle" fontSize={8.5} fill={color} fontWeight="700">
              To reject step disturbance: need integrator in path from D to Y
            </text>
            <text x={x+141} y={341} textAnchor="middle" fontSize={8} fill="#555">
              {x===18 ? 'High C(s) gain reduces R-error' : x===308 ? 'Integrator in C(s) before D rejects D' : 'Integrator in C(s) after N rejects N'}
            </text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Disturbances enter the loop at different points and must be analysed separately using superposition. An input disturbance D(s) (before the plant) is rejected if C(s) has an integrator, because the controller can sense and correct it. An output disturbance N(s) (after the plant) is harder to reject — the loop must sense it through H(s) and then correct through the full forward path. High loop gain at the disturbance frequency is the key to rejection.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 7 — Gain vs Type: Trade-offs and Stability
// ─────────────────────────────────────────────────────────────────
function DiagramGainVsType() {
  const W = 900, H = 380;
  const N = 250, tMax = 12;

  // Type 0 step responses with increasing gain K
  // G(s) = K/((s+1)(s+2)) — critically stable at K_max
  // Closed-loop poles computed via quadratic formula
  // s^2 + 3s + (2+K) = 0 → s = (-3 ± sqrt(9-4(2+K)))/2
  // Always stable (both poles in LHP for K>0)
  const kVals  = [1, 5, 20, 50];
  const kColors = ['#e07b00','#c0392b','#9b59b6','#2E75B6'];

  const type0Paths = kVals.map(K => {
    const a = 1, b = 3, c = 2 + K;
    const disc = b*b - 4*a*c;
    let d = '';
    for (let i = 0; i <= N; i++) {
      const t = i * tMax / N;
      let y;
      if (disc > 0) {
        const s1 = (-b + Math.sqrt(disc))/(2*a);
        const s2 = (-b - Math.sqrt(disc))/(2*a);
        const dcgain = K/c; // K/(2+K)
        y = dcgain + K/(s1*(s1-s2)) * Math.exp(s1*t) + K/(s2*(s2-s1)) * Math.exp(s2*t);
      } else if (disc === 0) {
        const s1 = -b/(2*a);
        y = K/c * (1 - (1 + (-s1)*t)*Math.exp(s1*t));
      } else {
        const sigma = b/(2*a);
        const wd    = Math.sqrt(-disc)/(2*a);
        const dcgain = K/c;
        y = dcgain * (1 - Math.exp(-sigma*t)*(Math.cos(wd*t) + sigma/wd*Math.sin(wd*t)));
      }
      const x = 30 + 32 + (t/tMax)*370;
      const yp = 30 + 270 - 20 - Math.min(Math.max(y,-0.1),2)*230;
      d += `${i===0?'M':'L'}${x.toFixed(1)},${yp.toFixed(1)} `;
    }
    return d.trim();
  });

  const kpVals  = kVals.map(K => K/2);
  const essVals = kVals.map(K => 1/(1 + K/2));

  // Type 1 step response (zero ess) for comparison
  // G(s) = Kv/s(s+2) → CL: s^2+2s+Kv=0 → y(t) known
  const type1Path = (() => {
    const Kv = 4;
    const sigma = 1, wd = Math.sqrt(Kv-1);
    let d = '';
    for (let i = 0; i <= N; i++) {
      const t = i * tMax / N;
      const y = 1 - Math.exp(-sigma*t)*(Math.cos(wd*t) + sigma/wd*Math.sin(wd*t));
      const x = 30 + 32 + (t/tMax)*370;
      const yp = 30 + 270 - 20 - y*230;
      d += `${i===0?'M':'L'}${x.toFixed(1)},${yp.toFixed(1)} `;
    }
    return d.trim();
  })();

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Gain vs System Type — Trade-offs in Reducing Steady-State Error</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arrowhead id="gvt-blk" color="#aaa"/>
          <clipPath id="gvt-clip"><rect x={60} y={30} width={378} height={275}/></clipPath>
        </defs>

        {/* ── LEFT: Step responses ── */}
        <rect x={28} y={10} width={434} height={310} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={245} y={26} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Step Response: Type 0 (varying K) vs Type 1</text>

        {/* Grid */}
        {[2,4,6,8,10,12].map(tv => (
          <line key={tv} x1={30+32+tv/tMax*370} y1={30} x2={30+32+tv/tMax*370} y2={305} stroke="#f8f8f8" strokeWidth="1"/>
        ))}
        {[0.5,1.0,1.5,2.0].map(yv => (
          <line key={yv} x1={62} y1={30+270-20-yv*230} x2={408} y2={30+270-20-yv*230} stroke="#f8f8f8" strokeWidth="1"/>
        ))}

        {/* Reference line */}
        <line x1={62} y1={30+270-20-230} x2={408} y2={30+270-20-230} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="7,5" opacity="0.7"/>
        <text x={412} y={30+270-20-226} fontSize={9} fill="#27ae60">r=1</text>

        {/* Axes */}
        <line x1={62} y1={282} x2={412} y2={282} stroke="#aaa" strokeWidth="1.4" markerEnd="url(#gvt-blk)"/>
        <line x1={62} y1={282} x2={62}  y2={28}  stroke="#aaa" strokeWidth="1.4" markerEnd="url(#gvt-blk)"/>
        <text x={416} y={286} fontSize={9} fill="#aaa">t</text>
        <text x={66}  y={25}  fontSize={9} fill="#aaa">y</text>

        {/* Type 0 curves */}
        {type0Paths.map((d, i) => (
          <path key={i} d={d} fill="none" stroke={kColors[i]} strokeWidth="2" clipPath="url(#gvt-clip)"/>
        ))}

        {/* Type 1 */}
        <path d={type1Path} fill="none" stroke="#27ae60" strokeWidth="2.8" clipPath="url(#gvt-clip)"/>

        {/* Legend */}
        {kVals.map((K, i) => {
          const ess = 1/(1+K/2);
          return (
            <g key={i}>
              <line x1={36} y1={298+i*10} x2={56} y2={298+i*10} stroke={kColors[i]} strokeWidth="2"/>
              <text x={60} y={302+i*10} fontSize={8.5} fill={kColors[i]}>Type 0, K={K} → e_ss={ess.toFixed(3)}</text>
            </g>
          );
        })}
        <line x1={36} y1={338} x2={56} y2={338} stroke="#27ae60" strokeWidth="2.8"/>
        <text x={60} y={342} fontSize={8.5} fill="#27ae60">Type 1, Kv=4 → e_ss=0 (zero error!)</text>

        {/* ── RIGHT: Trade-off analysis ── */}
        <rect x={474} y={10} width={416} height={370} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={682} y={28} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Trade-off Analysis</text>

        {/* Gain increase trade-offs */}
        <text x={482} y={48} fontSize={11} fill="#7B0D0D" fontWeight="700">Increasing Gain K (Type 0):</text>

        {[
          { pro: true,  txt: 'Reduces e_ss: 1/(1+Kp) → smaller as K→∞' },
          { pro: true,  txt: 'Faster rise time (bandwidth increases)' },
          { pro: false, txt: 'More overshoot (damping ratio decreases)' },
          { pro: false, txt: 'Reduced stability margins (phase margin drops)' },
          { pro: false, txt: 'Risk of instability for high K (Routh / GM)' },
          { pro: false, txt: 'e_ss → 0 only as K→∞, never actually zero' },
        ].map(({ pro, txt }, i) => (
          <g key={i}>
            <rect x={482} y={54+i*22} width={400} height={20} rx={2}
              fill={pro?'#f0fff4':'#FEF0F0'} stroke={pro?'#27ae60':'#c0392b'} strokeWidth="0.6"/>
            <text x={490} y={68+i*22} fontSize={9.5} fill={pro?'#27ae60':'#c0392b'}>{pro?'✓':'✗'} {txt}</text>
          </g>
        ))}

        <line x1={482} y1={198} x2={882} y2={198} stroke="#f0cece" strokeWidth="1"/>
        <text x={682} y={216} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Adding an Integrator (Type 0 → Type 1):</text>

        {[
          { pro: true,  txt: 'Zero steady-state error to step — exactly' },
          { pro: true,  txt: 'Zero velocity error for ramp tracking' },
          { pro: true,  txt: 'Disturbance rejection improves at low freq' },
          { pro: false, txt: 'Additional −90° phase lag reduces phase margin' },
          { pro: false, txt: 'May require re-tuning controller gains' },
          { pro: false, txt: 'Higher type → harder to stabilize (Nyquist)' },
        ].map(({ pro, txt }, i) => (
          <g key={i}>
            <rect x={482} y={222+i*22} width={400} height={20} rx={2}
              fill={pro?'#f0fff4':'#FEF0F0'} stroke={pro?'#27ae60':'#c0392b'} strokeWidth="0.6"/>
            <text x={490} y={236+i*22} fontSize={9.5} fill={pro?'#27ae60':'#c0392b'}>{pro?'✓':'✗'} {txt}</text>
          </g>
        ))}

        {/* Summary box */}
        <rect x={482} y={360} width={400} height={0} rx={3}/>

        {/* e_ss vs K table */}
        <rect x={482} y={360} width={400} height={0} rx={2}/>

        {/* e_ss bar chart */}
        <text x={682} y={364} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontWeight="700">e_ss for Type 0, G=K/((s+1)(s+2)):</text>
        {kVals.map((K, i) => {
          const ess = 1/(1+K/2);
          const barW = ess * 280;
          return (
            <g key={i}>
              <rect x={482} y={370+i*14} width={barW} height={12} rx={2} fill={kColors[i]} opacity="0.7"/>
              <text x={482+barW+4} y={380+i*14} fontSize={9} fill={kColors[i]}>K={K}: {ess.toFixed(4)}</text>
            </g>
          );
        })}
        <rect x={482} y={370} width={1} height={56} fill="#aaa"/>
        <text x={762} y={382} fontSize={9} fill="#27ae60" fontWeight="700">Type 1: 0.0000 ← integrator wins!</text>
      </svg>
      <div style={S.dCaption}>Increasing gain K reduces the steady-state error in a Type 0 system but never reaches zero — and risks instability. Adding an integrator (making the system Type 1) achieves exactly zero step-input error regardless of gain, at the cost of 90° additional phase lag. This illustrates the fundamental design choice: use gain for small improvements, use integrators for guaranteed zero steady-state error.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 8 — Complete SSE Summary: All Scenarios
// ─────────────────────────────────────────────────────────────────
function DiagramSummary() {
  const W = 900, H = 400;

  // Computed error vs gain curves for Type 0 (step) and Type 1 (ramp)
  const gainVals = [];
  for (let i = 0; i <= 80; i++) gainVals.push(0.1 + i * 0.6);

  const essStep0 = gainVals.map(K => 1/(1 + K));     // Type 0, Kp=K
  const essRamp1 = gainVals.map(K => 1/K);            // Type 1, Kv=K

  const ox0 = 30, oy0 = 30, gW = 370, gH = 160;
  const gsx = K  => ox0 + 32 + ((K - 0.1)/(gainVals[gainVals.length-1]-0.1)) * (gW-52);
  const gsy = e  => oy0 + gH - 20 - Math.min(e, 1.0) * (gH-30);

  const ess0Path = essStep0.map((e, i) =>
    `${i===0?'M':'L'}${gsx(gainVals[i]).toFixed(1)},${gsy(e).toFixed(1)}`).join(' ');
  const ess1Path = essRamp1.map((e, i) =>
    `${i===0?'M':'L'}${gsx(gainVals[i]).toFixed(1)},${gsy(e).toFixed(1)}`).join(' ');

  // Summary data for all combinations
  const summaryData = [
    { type:'Type 0', input:'Step',      formula:'1/(1+Kp)', depends:'Kp=G(0)',       finite:true  },
    { type:'Type 0', input:'Ramp',      formula:'∞',        depends:'Kv=0',           finite:false },
    { type:'Type 0', input:'Parabola',  formula:'∞',        depends:'Ka=0',           finite:false },
    { type:'Type 1', input:'Step',      formula:'0',        depends:'Kp=∞',           finite:true  },
    { type:'Type 1', input:'Ramp',      formula:'1/Kv',     depends:'Kv=lim sG(s)',   finite:true  },
    { type:'Type 1', input:'Parabola',  formula:'∞',        depends:'Ka=0',           finite:false },
    { type:'Type 2', input:'Step',      formula:'0',        depends:'Kp=∞',           finite:true  },
    { type:'Type 2', input:'Ramp',      formula:'0',        depends:'Kv=∞',           finite:true  },
    { type:'Type 2', input:'Parabola',  formula:'1/Ka',     depends:'Ka=lim s²G(s)',  finite:true  },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Complete Steady-State Error Summary — Error vs Gain Curves &amp; All Combinations</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arrowhead id="sum-blk" color="#aaa"/>
          <clipPath id="sum-c0"><rect x={62} y={32} width={gW-50} height={gH-18}/></clipPath>
        </defs>

        {/* ── LEFT: e_ss vs gain curves ── */}
        <rect x={ox0} y={oy0} width={gW} height={gH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox0+gW/2} y={oy0-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">e_ss vs Loop Gain K</text>

        {/* Grid */}
        {[10,20,30,40].map(kv => (
          <line key={kv} x1={gsx(kv)} y1={oy0+8} x2={gsx(kv)} y2={oy0+gH-18} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        {[0.25,0.5,0.75,1.0].map(ev => (
          <g key={ev}>
            <line x1={ox0+30} y1={gsy(ev)} x2={ox0+gW-14} y2={gsy(ev)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={ox0+26} y={gsy(ev)+3} textAnchor="end" fontSize={8} fill="#bbb">{ev}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={ox0+30} y1={oy0+gH-18} x2={ox0+gW-8} y2={oy0+gH-18} stroke="#aaa" strokeWidth="1.3" markerEnd="url(#sum-blk)"/>
        <line x1={ox0+30} y1={oy0+gH-18} x2={ox0+30}   y2={oy0+6}     stroke="#aaa" strokeWidth="1.3" markerEnd="url(#sum-blk)"/>
        <text x={ox0+gW-4} y={oy0+gH-14} fontSize={8} fill="#aaa">K</text>
        <text x={ox0+34}   y={oy0+8}      fontSize={8} fill="#aaa">e_ss</text>

        {/* Curves */}
        <path d={ess0Path} fill="none" stroke="#c0392b" strokeWidth="2.5" clipPath="url(#sum-c0)"/>
        <path d={ess1Path} fill="none" stroke="#2E75B6" strokeWidth="2.5" clipPath="url(#sum-c0)"/>

        {/* Asymptote (zero) */}
        <line x1={ox0+30} y1={gsy(0)} x2={ox0+gW-14} y2={gsy(0)} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="5,4"/>
        <text x={ox0+gW-10} y={gsy(0)-4} fontSize={8} fill="#27ae60">e=0</text>

        {/* Annotations */}
        <text x={gsx(8)}  y={gsy(essStep0[13])-8} fontSize={8.5} fill="#c0392b">Type 0 step: 1/(1+K)</text>
        <text x={gsx(10)} y={gsy(essRamp1[16])-8} fontSize={8.5} fill="#2E75B6">Type 1 ramp: 1/K</text>

        <text x={ox0+gW/2} y={oy0+gH-4} textAnchor="middle" fontSize={8} fill="#aaa">Both → 0 as K→∞ but Type 1/2 give exact 0</text>

        {/* ── BOTTOM LEFT: Complete reference matrix ── */}
        <rect x={ox0} y={oy0+gH+16} width={gW} height={175} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox0+gW/2} y={oy0+gH+32} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Complete Reference Matrix</text>

        {/* Header */}
        <rect x={ox0+2} y={oy0+gH+38} width={gW-4} height={20} rx={2} fill="#c0392b"/>
        {['Type','Input','e_ss','Error Const.'].map((h, i) => (
          <text key={i} x={ox0+[10,68,168,258][i]} y={oy0+gH+52} fontSize={9} fill="#fff" fontWeight="700">{h}</text>
        ))}

        {summaryData.map(({ type, input, formula, depends, finite }, i) => {
          const rowY = oy0+gH+58+i*13;
          const fc = formula==='0'?'#27ae60':formula==='∞'?'#c0392b':'#e07b00';
          const typeColor = type==='Type 0'?'#c0392b':type==='Type 1'?'#2E75B6':'#27ae60';
          return (
            <g key={i}>
              <rect x={ox0+2} y={rowY} width={gW-4} height={13} fill={i%2===0?'#fafafa':'#fff'} stroke="#f5f5f5" strokeWidth="0.5"/>
              <text x={ox0+10}  y={rowY+10} fontSize={8.5} fill={typeColor} fontWeight="700">{type}</text>
              <text x={ox0+68}  y={rowY+10} fontSize={8.5} fill="#333">{input}</text>
              <text x={ox0+168} y={rowY+10} fontSize={8.5} fill={fc} fontWeight="700" fontFamily="'Courier New',monospace">{formula}</text>
              <text x={ox0+258} y={rowY+10} fontSize={7.5} fill="#555" fontFamily="'Courier New',monospace">{depends}</text>
            </g>
          );
        })}

        {/* ── RIGHT: Methods to reduce SSE ── */}
        <rect x={420} y={oy0} width={470} height={370} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={655} y={oy0+18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Methods to Reduce or Eliminate Steady-State Error</text>

        {[
          {
            num:'①', title:'Increase Loop Gain K',
            color:'#e07b00', bg:'#fff5e6',
            pros:'Reduces e_ss = 1/(1+Kp)\nSimple to implement',
            cons:'Never achieves zero error\nReduces stability margins\nMay cause oscillation',
            when:'Quick improvement, small tolerance acceptable',
          },
          {
            num:'②', title:'Add Integrator to Forward Path (PI / I control)',
            color:'#c0392b', bg:'#FEF0F0',
            pros:'Zero steady-state error to step\nRejects constant disturbances',
            cons:'−90° phase lag: reduces PM\nSlower transient response\nAnti-windup needed',
            when:'Standard solution for position error; use PI controller',
          },
          {
            num:'③', title:'Add Second Integrator (Type 0 → Type 2)',
            color:'#2E75B6', bg:'#eaf3ff',
            pros:'Zero error for step AND ramp\nTracks accelerating refs',
            cons:'Very difficult to stabilize\nNeeds careful lead compensation',
            when:'Servo systems, trajectory following',
          },
          {
            num:'④', title:'Input Feedforward Compensation',
            color:'#27ae60', bg:'#f0fff4',
            pros:'Zero error without changing loop dynamics\nDoesn\'t affect stability',
            cons:'Requires accurate plant model\nSensitive to model errors',
            when:'When loop redesign not possible; model-based FF',
          },
          {
            num:'⑤', title:'Integral Windup Prevention',
            color:'#9b59b6', bg:'#f5d0ff',
            pros:'Maintains integrator benefits\nPrevents saturation issues',
            cons:'Requires additional logic\nBack-calculation or clamping',
            when:'Any PI/PID system with actuator limits',
          },
        ].map(({ num, title, color, bg, pros, cons, when }, i) => {
          const y = oy0+28+i*68;
          return (
            <g key={i}>
              <rect x={428} y={y} width={454} height={64} rx={3} fill={bg} stroke={color} strokeWidth="1"/>
              <text x={438} y={y+14} fontSize={11} fill={color} fontWeight="700">{num} {title}</text>
              <text x={438} y={y+28} fontSize={8.5} fill="#27ae60">✓ {pros.split('\n')[0]}</text>
              <text x={438} y={y+40} fontSize={8.5} fill="#c0392b">✗ {cons.split('\n')[0]}</text>
              <text x={438} y={y+52} fontSize={8}   fill="#555" fontStyle="italic">Use when: {when}</text>
              <text x={700} y={y+28} fontSize={8.5} fill="#27ae60">✓ {pros.split('\n')[1]??''}</text>
              <text x={700} y={y+40} fontSize={8.5} fill="#c0392b">✗ {cons.split('\n')[1]??''}</text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>The e_ss vs K curves (top left) show that both Type 0 (step error) and Type 1 (ramp error) tend toward zero as K→∞, but only reach exactly zero with higher system type. The complete reference matrix (bottom left) summarises all 9 input/type combinations. The five reduction methods on the right range from simple gain increase (quick but imperfect) to integral action (exact but requires careful stability design) to feedforward (powerful but model-dependent).</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Export
// ─────────────────────────────────────────────────────────────────
export default function SteadyStateError() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Control Systems — Steady-State Error</h1>
      <p style={S.p}>
        Steady-state error is the difference between the desired reference signal and the actual system output as time approaches infinity — after all transients have died away. Even a perfectly stable, well-damped system may fail its mission if it ends up pointing at the wrong place, moving at the wrong speed, or tracking the wrong trajectory. Understanding and eliminating steady-state error is therefore just as important as achieving stability and good transient response. The key analytical tools are the Final Value Theorem, the system type classification (number of open-loop integrators), and the three error constants: position Kp, velocity Kv, and acceleration Ka.
      </p>

      <InfoBox title="Core Concepts at a Glance" items={[
        "e_ss = lim[r(t)−y(t)] as t→∞ — the permanent offset between reference and output",
        "Final Value Theorem: e_ss = lim s·E(s) as s→0  (only valid when closed-loop is stable)",
        "Error TF: E(s)/R(s) = 1/(1+G(s)H(s)) for unity feedback systems",
        "System Type = number of free integrators in the open-loop TF G(s)H(s)",
        "Kp = lim G(s), Kv = lim s·G(s), Ka = lim s²·G(s)  all as s→0",
        "Type 0: finite position error, infinite velocity & acceleration errors",
        "Type 1: zero position error, finite velocity error, infinite acceleration error",
        "Type 2: zero position & velocity errors, finite acceleration error",
        "Non-unity feedback H(s)≠1: actuating signal error ≠ true output error",
        "Disturbances add additional error contributions — reject with high gain or integrators",
      ]}/>

      <h2 style={S.h2}>What Is Steady-State Error?</h2>
      <p style={S.p}>
        Steady-state error manifests differently depending on the input type. For a step (position) command the error is a constant gap between the commanded and achieved position. For a ramp (velocity) command the system eventually tracks at the right speed but lags behind by a fixed distance. For a parabolic (acceleration) command the error grows with time unless the system has enough integrators. These three scenarios define the three most important test cases for steady-state accuracy.
      </p>
      <DiagramWhatIsSSE/>
      <Table2 rows={[
        ["Steady-state error definition","e_ss = lim[r(t) − y(t)] as t → ∞\nRequires the system to be stable and the limit to exist\nMeasured after all transients have fully decayed"],
        ["Step input error","e_ss = lim[1 − y(t)] as t→∞\nAppears as a constant vertical gap between r=1 and y(∞)\nCaused by finite loop gain at DC (s=0)"],
        ["Ramp input error","e_ss = constant offset between r(t)=t and y(t)\nSystem follows the ramp but with a fixed time lag\nCaused by insufficient loop gain at very low frequencies"],
        ["Parabola input error","e_ss = lim[t²/2 − y(t)] — can grow with time\nSystem cannot maintain the acceleration\nRequires two integrators for zero error"],
        ["Why error exists","Open-loop gain is finite at DC → finite error signal needed to drive output\nZero error requires infinite DC gain → integrator in the loop"],
        ["When FVT applies","FVT lim s·E(s) as s→0 is valid ONLY if closed-loop is stable\nIf system is unstable, the error grows without bound — FVT gives wrong answer"],
        ["Error vs disturbance","Steady-state error includes contributions from reference tracking AND disturbances\nAnalysed separately using superposition then combined"],
        ["Practical implications","GPS navigation: position error → wrong location\nRobot joint: angle error → inaccurate positioning\nMotor speed: velocity error → wrong RPM under load"],
      ]}/>

      <h2 style={S.h2}>Final Value Theorem &amp; Error Transfer Function</h2>
      <p style={S.p}>
        The Final Value Theorem provides the algebraic bridge between the Laplace-domain expression for E(s) and the time-domain steady-state value. Combined with the closed-loop error transfer function, it reduces the problem of computing e_ss to evaluating a limit as s→0 — a purely algebraic operation requiring no inverse Laplace transforms.
      </p>
      <DiagramFVT/>
      <Table2 rows={[
        ["Error transfer function","E(s)/R(s) = 1/(1+G(s)H(s))\nDerived from: Y=GE, B=HY, E=R−B → eliminate Y and B\nValid for unity and non-unity feedback"],
        ["Final Value Theorem","lim f(t) = lim s·F(s)  as t→∞ and s→0\nCondition: s·F(s) must have all poles with Re(s) < 0\nEquivalent: closed-loop system must be stable"],
        ["Position error constant Kp","Kp = lim G(s)H(s)  as s→0\ne_ss(step) = 1/(1+Kp)\nKp=0: e_ss=1; Kp→∞: e_ss→0"],
        ["Velocity error constant Kv","Kv = lim s·G(s)H(s)  as s→0\ne_ss(ramp) = 1/Kv\nKv=0: infinite ramp error; Kv=∞: zero ramp error"],
        ["Acceleration error constant Ka","Ka = lim s²·G(s)H(s)  as s→0\ne_ss(parabola) = 1/Ka\nKa=0: infinite parabola error; Ka=∞: zero error"],
        ["FVT with step R(s)=1/s","e_ss = lim s·(1/s)·[1/(1+G(s)H(s))] = lim 1/(1+GH) = 1/(1+Kp)"],
        ["FVT with ramp R(s)=1/s²","e_ss = lim s·(1/s²)·[1/(1+GH)] = lim (1/s)·[1/(1+GH)] = 1/Kv"],
        ["Stability prerequisite","Always check closed-loop stability BEFORE applying FVT\nUnstable system: e(t)→∞, FVT gives a finite but meaningless answer"],
      ]}/>

      <h2 style={S.h2}>System Type Classification</h2>
      <p style={S.p}>
        The system type is the single most important parameter for predicting steady-state tracking accuracy. It counts the number of pure integrators (poles exactly at s=0) in the open-loop transfer function G(s)H(s). Each integrator provides one order of "free" gain at DC, which is what drives the corresponding error term to zero.
      </p>
      <DiagramSystemType/>
      <Table2 rows={[
        ["System type definition","Type N = number of poles at s=0 in open-loop G(s)H(s)\nType 0: G(s) = K·N(s)/D(s)  (no s in denominator)\nType 1: G(s) = K·N(s)/(s·D(s))  (one s factor)\nType 2: G(s) = K·N(s)/(s²·D(s))  (two s factors)"],
        ["Type 0 error constants","Kp = G(0) = K/(product of nonzero poles) — finite\nKv = lim s·G(s) = 0  →  ramp error = ∞\nKa = lim s²·G(s) = 0  →  parabola error = ∞"],
        ["Type 1 error constants","Kp = lim G(s) = ∞  →  step error = 0\nKv = lim s·G(s) = finite  →  ramp error = 1/Kv\nKa = 0  →  parabola error = ∞"],
        ["Type 2 error constants","Kp = ∞, Kv = ∞  →  step and ramp errors both = 0\nKa = lim s²·G(s) = finite  →  parabola error = 1/Ka"],
        ["Bode low-frequency slope","Type 0: 0 dB/decade (flat at low ω)\nType 1: −20 dB/decade (single integrator slope)\nType 2: −40 dB/decade (double integrator slope)\nThe Bode plot's low-frequency behaviour reveals system type"],
        ["Higher-order input tracking","Type N system tracks nth-order inputs with zero steady-state error\nType N−1 inputs: finite error  |  Type N−2 inputs: infinite error\nGeneral rule: need Type ≥ order of input polynomial"],
        ["Stability vs type trade-off","Each integrator adds −90° phase at all frequencies\nType 2 starts with −180° phase → need compensation to stabilise\nTypical maximum practical type: 2 (with care), rarely type 3+"],
        ["Identifying type from Bode","Count the −20 dB/decade increments on the low-frequency asymptote\nType 0: asymptote is flat (0 slope)\nType 1: asymptote has slope −1 (−20 dB/dec)"],
      ]}/>

      <h2 style={S.h2}>Error Constants Kp, Kv, Ka</h2>
      <p style={S.p}>
        The three static error coefficients completely characterise the steady-state tracking capability of a unity-feedback control system. They are computed directly from the open-loop transfer function by taking limits as s→0 — no simulation or time-domain analysis required. Their inverses give the steady-state error to the corresponding standard input.
      </p>
      <DiagramErrorConstants/>
      <Table2 rows={[
        ["Computing Kp","Kp = lim G(s)H(s)  as s→0\nFor Type 0: Kp = G(0) (finite DC gain)\nFor Type ≥1: Kp = ∞ (integrator makes DC gain infinite)"],
        ["Computing Kv","Kv = lim s·G(s)H(s)  as s→0\nFor Type 0: Kv = 0 (s kills the finite term)\nFor Type 1: Kv = K/(product of non-zero poles at s=0)\nFor Type ≥2: Kv = ∞"],
        ["Computing Ka","Ka = lim s²·G(s)H(s)  as s→0\nFor Type 0 or 1: Ka = 0\nFor Type 2: Ka = K/(product of non-zero poles)\nFor Type ≥3: Ka = ∞"],
        ["Worked example: G(s)=10/s(s+5)","Type 1 system\nKp = ∞  →  e_ss(step) = 0\nKv = lim s·[10/s(s+5)] = 10/5 = 2  →  e_ss(ramp) = 0.5\nKa = lim s²·[10/s(s+5)] = 0  →  e_ss(parabola) = ∞"],
        ["Effect of gain on Kv","G(s) = K/s(s+a)  →  Kv = K/a\nDoubling K doubles Kv → halves ramp error\nKv depends linearly on the open-loop gain K"],
        ["Effect of gain on Kp","G(s) = K/(s+a)(s+b)  →  Kp = K/(ab)\ne_ss = 1/(1+K/ab) → K→∞ gives e_ss→0 but never =0\nGain can reduce but not eliminate Type 0 step error"],
        ["Units of error constants","Kp: dimensionless (position gain)\nKv: [rad/s] or [1/time] — inverse of time lag\nKa: [rad²/s²] or [1/time²] — inverse of time²"],
        ["Error constant table lookup","Given system, compute type: determines which constants are finite\nCompute the finite constant: determines error magnitude\nIf constant is 0: error is ∞ for that input"],
      ]}/>

      <h2 style={S.h2}>Non-Unity Feedback Systems</h2>
      <p style={S.p}>
        When the feedback sensor H(s) is not unity — for example a tachometer with gain 10 V·s/rad, or a transducer with filtering — the error signal at the summing junction is no longer equal to the actual output error. This distinction is critical: a controller that drives its actuating error E(s) to zero may still leave a significant error between r(t) and y(t).
      </p>
      <DiagramNonUnity/>
      <Table2 rows={[
        ["Actuating signal error","E(s) = R(s) − H(s)Y(s)  — difference at summing junction\nThis is what the controller measures and minimizes\nFor H(s)=1: coincides with true output error"],
        ["True output error","ε(s) = R(s) − Y(s)  — actual tracking error\nε(s)/R(s) = [1+G(s)H(s)−G(s)] / [1+G(s)H(s)]\nFor H(s)≠1: differs from E(s)"],
        ["DC sensor gain H(0)","If H(0)=1 (unity DC gain): true output error = actuating error\nIf H(0)=2 (sensor reads 2×): controller nulls B=HY=2Y=R → Y=R/2\nPermanent 50% output error even with zero actuating error!"],
        ["Reduction to unity feedback","Add gain 1/H(s) in forward path before summing junction\nThis makes the effective feedback unity: cancels H(s)\nRequires exact knowledge of H(s) — sensitive to model error"],
        ["Generalised e_ss formula","e_ss = lim s·R(s)·[1+G(s)(H(s)−1)] / [1+G(s)H(s)]  as s→0\nFor H=1: reduces to standard 1/(1+GH)·lim s·R(s)"],
        ["Sensor with known DC gain","If H(0)=h₀ (constant): scale reference by h₀ to precompensate\nR_effective = h₀·R gives Y→R at steady state\nCommon in speed control with tachometers"],
        ["Low-pass sensor H(s)=1/(τ_s·s+1)","H(0)=1: no DC output error\nBut adds dynamics: phase lag at high frequencies\nCan affect stability margins even if steady-state is fine"],
        ["Cascade compensation for H(s)≠1","Add prefilter P(s) = 1/H(s) in reference path\nMakes effective input R_eff = R/H(s)·H(s) = R\nExact cancellation requires accurate H(s)"],
      ]}/>

      <h2 style={S.h2}>Steady-State Error Due to Disturbances</h2>
      <p style={S.p}>
        In real systems, disturbances — wind forces on an antenna, load torque on a motor, sensor noise — enter the loop at various points and create their own steady-state errors. These are analysed by superposition: set the reference to zero, apply only the disturbance, and compute the resulting steady-state output using the Final Value Theorem. The disturbance transfer function depends critically on where the disturbance enters relative to the controller.
      </p>
      <DiagramDisturbances/>
      <Table2 rows={[
        ["Superposition principle","Total output: Y = T_R·R + T_D·D + T_N·N\nSet all inputs but one to zero, compute each contribution\nAdd all contributions for total steady-state error"],
        ["Input disturbance D(s)","Enters between controller and plant\nY_D/D = G₁(s)G₂(s)/[1+C(s)G₁(s)G₂(s)H(s)]\nFor step D: e_ss(D) = G₁G₂/[1+CG₁G₂H] evaluated at s→0"],
        ["Output disturbance N(s)","Enters at the output (wind, process noise)\nY_N/N = G₂(s)/[1+C(s)G₁(s)G₂(s)H(s)]\nFor step N: e_ss(N) = G₂(0)/[1+C(0)G₁(0)G₂(0)H(0)]"],
        ["Rejecting input disturbances","Need integrator in C(s) between R-summing and D-injection point\nIntegrator in C: as s→0, C(s)→∞ → makes denominator→∞ → error→0\nPI or I controller rejects constant input disturbances"],
        ["Rejecting output disturbances","Need high loop gain C(s)G₁(s)G₂(s)H(s) at disturbance frequency\nFor constant N: e_ss(N) = 1/[1+C(0)·G₁(0)·G₂(0)·H(0)]\nIntegrator anywhere in forward path helps"],
        ["Sensor noise","N(s) treated as output disturbance\nHigh gain → rejects process disturbances but amplifies sensor noise\nFundamental trade-off: disturbance rejection vs noise amplification"],
        ["Reference tracking vs disturbance rejection","Both improved by high loop gain — not in conflict at DC\nConflict at high frequencies: high BW needed for tracking, low BW for noise\nIntegral action resolves the DC conflict without bandwidth sacrifice"],
        ["Feedforward disturbance rejection","Measure disturbance directly; compute cancellation signal\ne_ss → 0 without changing loop dynamics\nRequires measurable disturbance and accurate model"],
      ]}/>

      <h2 style={S.h2}>Gain vs System Type — The Design Trade-off</h2>
      <p style={S.p}>
        The engineer has two fundamental levers for reducing steady-state error: increase the loop gain K, or increase the system type by adding an integrator. These strategies have very different consequences for stability, transient response, and robustness. Understanding this trade-off is central to practical controller design.
      </p>
      <DiagramGainVsType/>
      <Table2 rows={[
        ["Gain increase effect on SSE","e_ss(step, Type 0) = 1/(1+Kp) → Kp = K·G_normalized(0)\nDoubling K halves the error\nTheoretical e_ss→0 only as K→∞ — never achieved in practice"],
        ["Gain increase effect on stability","Phase margin: PM ≈ 90° − ∠G(jω_gc) → decreases as K increases\nGain margin: GM = 1/|G(jω_pc)| → decreases as K increases\nExcessive gain → oscillation or instability"],
        ["Integral action (I or PI)","Adds pole at s=0: Type 0 → Type 1\nStep error: 1/(1+Kp) → 0 exactly\nCost: −90° phase contribution at all frequencies"],
        ["Anti-windup for integrators","Problem: if actuator saturates, integrator accumulates (winds up)\nResult: large overshoot when system exits saturation\nSolution: clamp integration when saturated, or back-calculation"],
        ["PID with integral","I term: steady-state error elimination\nP term: speed up response, initial error correction\nD term: damping — counters the phase lag from I\nTuning: choose Kp, Ki (=Kp/Ti), Kd (=Kp·Td)"],
        ["Feedforward for zero error","Add R(s)·[1/G(s)] in parallel with C(s)\nCompensates for plant gain: y→r at DC without integration\nModel-dependent: requires accurate plant knowledge"],
        ["Robust integral — BIBO","Integral action robust to gain variations: error stays zero\nAS long as closed-loop remains stable, e_ss=0 for step\nGain changes affect transient not steady-state"],
        ["Design procedure","1. Choose type to match input requirement\n2. Check stability of that type\n3. Tune gain for desired transient\n4. Verify stability margins remain adequate"],
      ]}/>

      <h2 style={S.h2}>Complete Reference Summary</h2>
      <p style={S.p}>
        The final diagram brings together all key concepts: the e_ss vs gain curves showing the hyperbolic decay of error with gain, the complete 9-cell type/input matrix, and the five practical methods for error reduction with their trade-offs.
      </p>
      <DiagramSummary/>
      <Table2 rows={[
        ["Summary: Type 0 system","Kp finite, Kv=0, Ka=0\nStep: e_ss=1/(1+Kp)  |  Ramp: ∞  |  Parabola: ∞\nExample: thermostat, simple proportional position control"],
        ["Summary: Type 1 system","Kp=∞, Kv finite, Ka=0\nStep: 0  |  Ramp: 1/Kv  |  Parabola: ∞\nExample: motor speed control with tachometer feedback, PI controller"],
        ["Summary: Type 2 system","Kp=∞, Kv=∞, Ka finite\nStep: 0  |  Ramp: 0  |  Parabola: 1/Ka\nExample: position control with two integrators, tracking servo"],
        ["Quick identification method","1. Write G(s)H(s) in factored form\n2. Count factors of s in denominator → that is the type\n3. Compute the relevant error constant for the input of interest"],
        ["Key design equations","ζ → from %OS  |  ωₙ → from Ts  |  poles at −ζωₙ±jωd\ne_ss → from type and error constant  |  PM from Bode\nAll four must be simultaneously satisfied"],
        ["Internal Model Principle","To track r(t) with zero steady-state error, the loop must contain\na model of the signal generator producing r(t)\nStep → integrator (pole at 0); sinusoid → poles at ±jω₀\nThis is the theoretical foundation of integral action"],
        ["Sensitivity function","S(s) = E(s)/R(s) = 1/(1+G(s)H(s))  = error TF\nAt s=0: S(0) = 0 for Type≥1 (perfect DC rejection)\nS(jω) < 1 inside BW; > 1 near crossover (waterbed effect)"],
        ["Complementary sensitivity","T(s) = 1 − S(s) = G(s)H(s)/(1+G(s)H(s))  = closed-loop TF\nS + T = 1  always\nS small (good tracking) ↔ T≈1 (pass-through) — not a conflict at DC"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Error TF: E(s)/R(s) = 1/(1+G(s)H(s))  (unity or non-unity feedback)",
        "FVT: e_ss = lim s·E(s) as s→0  (valid only when closed-loop is stable)",
        "Kp = lim G(s)H(s)|s→0,  Kv = lim s·G(s)H(s)|s→0,  Ka = lim s²·G(s)H(s)|s→0",
        "Step error: 1/(1+Kp)  |  Ramp error: 1/Kv  |  Parabola error: 1/Ka",
        "Type 0: Kv=Ka=0 (∞ error for ramp/parabola)  |  Type 1: Ka=0 (∞ for parabola)",
        "Non-unity: true output e_ss = lim s·R(s)·[1+G(s)(H(s)−1)]/[1+G(s)H(s)]",
        "Disturbance step: e_ss(D) = G(s_path)/(1+loop gain) evaluated at s→0",
        "Zero steady-state error to step: need at least Type 1 (integrator in forward path)",
      ]}/>
    </div>
  );
}